import { Button, Input, MagellanPopup } from 'components/atoms';
import LabeledDeliveryStatus from 'components/molecules/labeledStatus/LabeledDeliveryStatus';
import { useEffect, useMemo, useRef, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  parseDeliveryStatusId,
  toDeliveryStatusId,
  normalizeDeliveryStatusIds,
} from 'services/configuration/notifications/constants';
import { DELIVERY_STATUS } from 'services/music-product-digital/types/enums';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { enumToObject } from 'utils/global/object.utils';
import './DeliveryStatusNotificationPopup.scss';

interface IDeliveryStatusNotificationPopupProps {
  trigger: JSX.Element;
  deliveryStatusIds: string[];
  onSave: (deliveryStatusIds: string[] | null) => void;
}

const DeliveryStatusNotificationPopup = ({
  trigger,
  deliveryStatusIds,
  onSave,
}: IDeliveryStatusNotificationPopupProps) => {
  const { t } = useTranslation();
  const popupRef = useRef<PopupActions>(null);
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>(() =>
    normalizeDeliveryStatusIds(deliveryStatusIds),
  );

  const deliveryStatuses = useMemo(
    () =>
      enumToObject(DELIVERY_STATUS).filter(
        ({ value }) => Number(value) !== DELIVERY_STATUS.unknown,
      ),
    [],
  );

  useEffect(() => {
    setSelectedStatusIds(normalizeDeliveryStatusIds(deliveryStatusIds));
  }, [deliveryStatusIds]);

  const toggleStatus = (statusId: string, checked: boolean) => {
    setSelectedStatusIds(current =>
      checked
        ? Array.from(new Set([...current, statusId]))
        : current.filter(id => id !== statusId),
    );
  };

  const handleApply = () => {
    onSave(selectedStatusIds.length ? selectedStatusIds : null);
    popupRef.current?.close();
  };

  const handleCancel = () => {
    setSelectedStatusIds(normalizeDeliveryStatusIds(deliveryStatusIds));
    popupRef.current?.close();
  };

  return (
    <Popup
      ref={popupRef}
      modal
      nested
      trigger={trigger}
      onOpen={() =>
        setSelectedStatusIds(normalizeDeliveryStatusIds(deliveryStatusIds))
      }>
      <MagellanPopup className='delivery-status-notification-popup-modal'>
        <MagellanPopup.Header className='delivery-status-notification-popup__header mb-4'>
          {t('notifications.delivery-status-popup-title')}
        </MagellanPopup.Header>
        <div className="delivery-status-notification-popup">
          <p className="delivery-status-notification-popup__description">
            {t('notifications.delivery-status-description')}
          </p>
          <div className="delivery-status-notification-popup__list">
          {deliveryStatuses.map(({ key, value }) => {
            const statusId = toDeliveryStatusId(value as DELIVERY_STATUS);
            const isChecked = selectedStatusIds.includes(statusId);

            return (
              <label
                key={key}
                className='delivery-status-notification-popup__item'>
                <Input.Checkbox
                  name={`delivery-status-${statusId}`}
                  checked={isChecked}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    toggleStatus(statusId, e.target.checked)
                  }
                />
                <LabeledDeliveryStatus
                  status={parseDeliveryStatusId(statusId)}
                />
              </label>
            );
          })}
          </div>
        </div>
        <MagellanPopup.BottomButtons>
          <Button type='button' isClear onClick={handleCancel}>
            <Button.Text text={t('button.cancel')} />
          </Button>
          <Button
            type='button'
            variant={BUTTON_VARIANT.submit}
            onClick={handleApply}>
            <Button.Text text={t('button.apply')} />
          </Button>
        </MagellanPopup.BottomButtons>
      </MagellanPopup>
    </Popup>
  );
};

export default DeliveryStatusNotificationPopup;
